import { HttpInterceptorFn, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { content } from 'src/environments/data';
import { ObservableCookieService } from './utils/observable-cookie.service';
import { UserService } from './services/user.service';
import { bearerTokenFromHeader } from './utils/ddn-session';

/** Context token to bypass automatic 401 logout (e.g. for credential checks, OTP verify). */
export const SKIP_AUTO_LOGOUT = new HttpContextToken<boolean>(() => false);

/** Credential / auth-flow 401s — not expired sessions */
const AUTH_CREDENTIAL_401_URLS = [
  '/auth/voice-login',
  '/auth/voice-login-multiple',
  '/auth/voice-login-liveness',
  '/auth/login',
  '/auth/send-otp',
  '/auth/verify-otp',
  '/auth/register-voice',
  '/auth/register-voice-multiple',
  '/auth/voice-enroll-incomplete',
  '/auth/voice-complete-signup',
  '/auth/link-voice-to-account',
  '/admin/impersonate',
  '/profile',
  '/change-password',
  '/api/test',
  '/health-fitness',
  '/finance-life',
  '/getUserSubscriptions',
  '/getUserScheduledSubscriptions',
  '/getUserCustomerId',
  '/subscriptions',
];

function sanitizeReturnUrl(currentUrl: string, loginPath: string): string {
  const login = '/' + loginPath.replace(/^\//, '');
  if (!currentUrl || currentUrl === '/' || currentUrl === login) {
    return '/';
  }
  // Already on login (with or without nested returnUrl query)
  if (currentUrl === login || currentUrl.startsWith(login + '?') || currentUrl.startsWith(login + '/')) {
    return '/';
  }
  // Nested / encoded login returnUrls
  try {
    let decoded = currentUrl;
    for (let i = 0; i < 5; i++) {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    }
    if (decoded.includes(login)) {
      return '/';
    }
  } catch {
    /* ignore */
  }
  return currentUrl;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const cookieService = inject(ObservableCookieService);
  const userService = inject(UserService);

  const webddn_login_path = content.WEBDDN_LOGIN_PATH;
  const loginRoute = '/' + webddn_login_path.replace(/^\//, '');
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        const skipLogoutByContext = req.context.get(SKIP_AUTO_LOGOUT);
        // Mid-signup accounts (e.g. a brand-new Google sign-up still completing
        // the registration form) legitimately 401 on incidental background calls
        // (notifications, hybrid token, SSE, ...) before their profile/role is
        // fully provisioned. That's not an invalid/expired session — don't nuke it.
        const isIncompleteSignupSession = userService.getCurrentUser()?.signup_complete === false;
        const shouldSkipLogout =
          skipLogoutByContext ||
          isIncompleteSignupSession ||
          AUTH_CREDENTIAL_401_URLS.some((url) => req.url.includes(url));
        const sentBearer = !!bearerTokenFromHeader(req.headers.get('Authorization'));
        const onLoginPage =
          router.url === loginRoute ||
          router.url.startsWith(loginRoute + '?') ||
          router.url.startsWith(loginRoute + '/');

        if (!shouldSkipLogout && !onLoginPage && sentBearer) {
          console.warn('401 Unauthorized - Logging out user for:', req.url);

          cookieService.deleteCookie('ddnsession');
          userService.clearUser();
          const returnUrl = sanitizeReturnUrl(router.url, webddn_login_path);

          router.navigate(['/' + webddn_login_path], {
            queryParams: returnUrl && returnUrl !== '/' ? { returnUrl } : {},
          });
        }
      }
      return throwError(() => error);
    })
  );
};
